import * as React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ApiError, NetworkError } from '@/lib/api';
import {
  errorKeyFor as defaultResolver,
  formatErrorMessage,
  type ErrorResolver,
} from '@/lib/form/errorMessages';

export interface FormErrorBannerProps {
  /** The error to display (typically a TanStack Query mutation.error). */
  error: unknown;
  /**
   * Resolver mapping the error to an i18n key. Defaults to the shared baseline;
   * pass a feature-specific resolver to surface domain errors.
   */
  resolveKey?: ErrorResolver;
  /** Hide when there's nothing to show. Defaults to true. */
  hideEmpty?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * The one concrete detail worth printing under the message, when there is one:
 * the request id a server fault can be looked up by, or — in development only —
 * a reminder of what an unreachable API usually means locally. Neither is
 * guesswork; both point at where to look next.
 */
function errorDetail(error: unknown, t: TFunction): string | null {
  if (error instanceof NetworkError && !error.offline && import.meta.env.DEV) {
    return t('errors.devApiHint');
  }
  if (error instanceof ApiError && error.requestId) {
    return t('errors.reference', { requestId: error.requestId });
  }
  return null;
}

/**
 * Banner for surfacing API mutation errors in a form. Translates a resolved
 * i18n key, and adds the request id or the local API hint underneath when the
 * error carries one. Renders nothing when `error` is falsy and `hideEmpty` is
 * true (default).
 */
export function FormErrorBanner({
  error,
  resolveKey = defaultResolver,
  hideEmpty = true,
  className,
  children,
}: FormErrorBannerProps) {
  const { t } = useTranslation();
  if (!error && hideEmpty) return null;
  const message = error ? formatErrorMessage(t, resolveKey(error)) : null;
  const detail = error ? errorDetail(error, t) : null;
  return (
    <div
      role="alert"
      className={[
        'rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children ?? (
        <>
          {message}
          {/* Custom children own their whole message, so the detail rides
              along only with the resolved one. */}
          {detail ? <span className="mt-1 block text-xs opacity-80">{detail}</span> : null}
        </>
      )}
    </div>
  );
}

export interface FormSuccessBannerProps {
  show: boolean;
  className?: string;
  children: React.ReactNode;
}

export function FormSuccessBanner({ show, className, children }: FormSuccessBannerProps) {
  if (!show) return null;
  return (
    <div
      role="status"
      className={[
        'rounded-md border border-primary/30 bg-primary/10 p-2 text-sm text-primary',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
